import { useEffect, useRef, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ProductionSidebar from "@/components/ProductionSidebar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { parsePolygon } from "@/types/domain";
import { getMapboxToken, formatDistance, calculateDistance, geocodeAddress } from "@/lib/mapbox";
import { isOrphanedTrackingLayer, isOrphanedTrackingSource, isMapReady } from "@/lib/mapboxLayers";
import { addZonePolygonLayers, removeZonePolygonLayers, resolveZoneLabel } from "@/lib/zoneStyle";
import { selectVisibleTrackingAreas } from "@/lib/trackingAreas";
import {
  createCrewMarkerElement,
  updateCrewMarkerElement,
  attachCrewMarkerHover,
} from "@/lib/crewMarkerElement";
import { buildCrewMarkerPopupHtml, type CrewPopupAssignment } from "@/lib/crewMarkerPopup";
import { useProducerLocationMarker } from "@/hooks/useProducerLocationMarker";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { useCrewShiftProgress, type CrewShiftAssignment } from "@/hooks/useCrewShiftProgress";
import { dedupeLatestLocationByUser } from "@/lib/dedupeLatestLocationByUser";
import { wallClockToUtc, BRIEFING_TZ_DEFAULT } from "@/lib/briefingTime";
import { effectiveShiftStartParts, effectiveShiftEndParts } from "@produce/shared";
import {
  areaMatchesStage,
  getInitials,
  hasLiveMapAccess,
  hasStageMapAccess,
} from "@produce/shared";
import { markerOpacityForAge, assignmentTitle, CREW_MARKER_SIZE } from "@produce/shared";
import { defaultEventDate, festivalDayIso, eventDatesBetween } from "@produce/shared";
import {
  allCrewMembersForDayQuery,
  crewTrackingConsentQuery,
  mapSettingsQuery,
  producerBriefingsQuery,
  trackingFestivalAreasQuery,
} from "@/lib/queries/productionQueries";
import { useCrewAvatars } from "@/hooks/useCrewAvatars";
import {
  crewTrackingStatus,
  crewTrackingLabel,
  crewTrackingExplanation,
  crewTrackingBadgeClass,
} from "@/lib/crewTrackingStatus";
import { CrewMapAvatar } from "@/components/CrewMapAvatar";
import { CrewShiftLines } from "@/components/CrewShiftLines";
import { useAuth } from "@/contexts/AuthContext";
import { useProductionTierAccess } from "@/hooks/useProductionTierAccess";
import {
  MapPin,
  Clock,
  Navigation,
  Crosshair,
  Lock,
  Edit,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { EditProductionFAB } from "@/components/EditProductionFAB";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { CrewNextUpSheet } from "@/components/CrewNextUpSheet";
import { useSidebar } from "@/contexts/SidebarContext";
import { PageTransition } from "@/components/PageTransition";
import { useProducerSettings } from "@/hooks/useProducerSettings";
import * as turf from "@turf/turf";

interface CrewLocation {
  id: string;
  user_id: string;
  briefing_id: string;
  latitude: number; // raw from GPS
  longitude: number; // raw from GPS
  display_lat: number; // raw or corrected (per crew_locations_display view)
  display_lng: number;
  timestamp: string;
  // How stale this fix is, per the crew_locations_display view. The map
  // fades a marker as it ages; the numeric GPS diagnostics the view also
  // exposes (correction source, confidence, correction magnitude) are
  // deliberately neither fetched nor shown — they described how the
  // marker was placed, not where the crew member is.
  age_seconds: number | null;
  crew_member: {
    id: string;
    name: string;
    function: string;
    color: string | null;
  };
}

/**
 * How long to wait for the zones + map settings before building the map
 * anyway. Long enough that the normal (cached or fast) path still builds once,
 * in place; short enough that a stalled read never leaves an empty page.
 */
const CAMERA_DATA_GRACE_MS = 2500;

const LiveTracking = () => {
  const navigate = useNavigate();
  const { productionId: briefingId } = useParams();
  const { session } = useAuth();
  const { urlFor: avatarUrlFor, avatars: crewAvatars } = useCrewAvatars(briefingId);
  const { expanded } = useSidebar();
  // Honours the producer's 12h/24h setting — the marker popups rendered a raw
  // HH:MM cut while the crew list beside them already followed the setting.
  const formatTime = useProducerTimeFormatter();
  const { settings: producerSettings } = useProducerSettings();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const isMounted = useRef(true);
  const isInitializing = useRef(false);
  const markers = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const currentAreaIds = useRef<string[]>([]); // Track which area IDs are currently on the map
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedCrewMemberId, setSelectedCrewMemberId] = useState<string | null>(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [crewFarFromStage, setCrewFarFromStage] = useState<Set<string>>(new Set());
  const [mapLoaded, setMapLoaded] = useState(false);
  // Producer's own device location + blue-dot marker (shared hook, also
  // used by the zone setup map). Returns the location for the "center on
  // me" button and the sidebar distance readout below.
  const producerLocation = useProducerLocationMarker(
    map,
    isMapReady({ hasMap: !!map.current, mapLoaded }),
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Live tracking access (PRO/STUDIO). A co-producer inherits the owner's plan.
  // Lock A: the map surface — zones, own position. From LITE.
  const { hasAccess: hasLiveTrackingAccess, isLoading: isAccessLoading } = useProductionTierAccess(
    briefingId,
    hasStageMapAccess,
  );
  // Lock B: live positions of crew. PRO/STUDIO — the crew_locations RLS
  // enforces the same rule, so below it this page would only ever receive
  // the producer's own row back.
  const { hasAccess: hasLiveCrew } = useProductionTierAccess(
    briefingId,
    hasLiveMapAccess,
    "briefing",
  );

  // Redirect if no production selected
  useEffect(() => {
    if (!briefingId) {
      navigate("/dashboard");
    }
  }, [briefingId, navigate]);

  // Fetch briefings for dropdown
  const { data: briefings } = useQuery({
    ...producerBriefingsQuery(session?.user?.id),
    enabled: !!session?.user?.id,
  });

  // Get current briefing details
  const currentBriefing = briefings?.find((b) => b.id === briefingId);
  const briefingTz = currentBriefing?.timezone || BRIEFING_TZ_DEFAULT;

  // Get event days for multi-day selector
  const eventDays = eventDatesBetween(
    currentBriefing?.festival_date_start,
    currentBriefing?.festival_date_end,
  );

  // Auto-select day: today if within range, else first day
  useEffect(() => {
    if (eventDays.length > 0 && !selectedDay) {
      // Festival day, not calendar day: a 00:30 shift belongs to yesterday's
      // event_date, and the producer may be watching from another timezone.
      setSelectedDay(defaultEventDate(eventDays, festivalDayIso(new Date(), briefingTz)));
    }
  }, [eventDays, selectedDay, briefingTz]);

  // Auto-select most recent briefing if none selected
  useEffect(() => {
    if (briefings && briefings.length > 0 && !briefingId) {
      navigate(`/live-tracking?briefing=${briefings[0].id}`, { replace: true });
    }
  }, [briefings, briefingId, navigate]);

  // Fetch map settings
  const { data: mapSettings, isFetched: mapSettingsFetched } = useQuery({
    ...mapSettingsQuery(briefingId),
    enabled: !!briefingId,
  });

  // Fetch crew locations with manual join
  const {
    data: crewLocations,
    refetch: refetchLocations,
    isFetching: isLocationsFetching,
  } = useQuery({
    queryKey: ["crew-locations", briefingId, selectedDay],
    queryFn: async () => {
      if (!briefingId || !selectedDay) return [];

      // Read from crew_locations_display view (one row per user with
      // corrections applied transparently — see migration
      // 20260427250000_crew_locations_display_view).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: locations, error: locError } = await (
        supabase.from("crew_locations_display" as any) as any
      )
        .select(
          "id, user_id, briefing_id, raw_lat, raw_lng, display_lat, display_lng, " +
            "timestamp, age_seconds",
        )
        .eq("briefing_id", briefingId)
        .order("timestamp", { ascending: false });

      if (locError) throw locError;
      if (!locations || locations.length === 0) return [];

      const userIds = locations.map((loc: { user_id: string }) => loc.user_id);

      const { data: crewMembers, error: crewError } = await supabase
        .from("crew_members")
        .select("user_id, name, function, color, id")
        .eq("briefing_id", briefingId)
        .eq("event_date", selectedDay)
        .in("user_id", userIds);

      if (crewError) throw crewError;

      const crewMemberMap = new Map(crewMembers?.map((cm) => [cm.user_id, cm]) || []);

      const mapped = locations
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((loc: any) => crewMemberMap.has(loc.user_id))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((loc: any) => ({
          // Backwards compat: keep latitude/longitude as the *raw* values.
          // The view also returns display_lat/lng for marker positioning.
          ...loc,
          latitude: loc.raw_lat,
          longitude: loc.raw_lng,
          crew_member: crewMemberMap.get(loc.user_id),
        })) as CrewLocation[];

      // Defensive: the view is contracted to one row per user, but markers
      // are keyed by user_id — any duplicate row would strand an orphan
      // marker on the map. Collapse to the latest fix per user.
      return dedupeLatestLocationByUser(mapped);
    },
    enabled: !!briefingId && !!session?.user?.id && !!selectedDay && hasLiveCrew,
    // 5s polling. Crew_locations was dropped from the supabase_realtime
    // publication in migration 20260508160000 to cut WAL decoder load
    // during festivals. Manual refresh button remains for instant
    // updates on demand.
    refetchInterval: (query) => {
      if (!session?.user?.id) return false;
      return query.state.data !== undefined ? 5000 : false;
    },
  });

  // Fetch ALL crew members for the selected day (regardless of GPS status)
  const { data: allCrewMembers } = useQuery({
    ...allCrewMembersForDayQuery(briefingId, selectedDay),
    enabled: !!briefingId && !!selectedDay,
  });

  // Who is sharing their location and who switched it off. Without this the
  // list cannot tell a deliberate opt-out from a dead phone.
  const { data: trackingConsent = [] } = useQuery({
    ...crewTrackingConsentQuery(briefingId),
    enabled: !!briefingId && hasLiveCrew,
  });
  // Keyed for O(1) lookup in the crew list below; the row shape goes straight
  // through, so "consent" has one spelling from SQL to badge.
  const consentByUserId = useMemo(
    () => new Map(trackingConsent.map((row) => [row.user_id, row])),
    [trackingConsent],
  );

  // What every crew member is on right now, and what follows it. Feeds
  // the marker progress rings, the crew list beside the map and the
  // marker popup — one source, so they cannot disagree.
  const { shiftStateByCrewMember } = useCrewShiftProgress(briefingId, selectedDay, briefingTz);

  // Which day's stage polygons to show: the position of the selected day
  // in the production's day list, which is what festival_areas.day_index
  // counts. The crew app's tracking map reads the same list.
  const dayIndex = Math.max(0, eventDays.indexOf(selectedDay));

  // Fetch festival areas (polygons) - fetch ALL stage zones, filter client-side for day fallback.
  // Private cache namespace ("producer-tracking") so this read-only map never
  // inherits another page's superset — Analytics fills ["festival-areas", id]
  // via select("*") (festival_boundary included), which used to leak the
  // boundary onto this map. selectVisibleTrackingAreas is the belt-and-braces
  // guard; the namespace is the root-cause fix. LiveTrackingSetup's
  // invalidateQueries(["festival-areas"]) still reaches this by prefix.
  const { data: festivalAreas = [], isFetched: festivalAreasFetched } = useQuery({
    ...trackingFestivalAreasQuery(briefingId),
    enabled: !!briefingId,
    staleTime: 5 * 60 * 1000, // Festival areas don't change frequently
  });

  // Stage zones for the selected day (day-0 fallback) + always-on location
  // zones. Shared allowlist helper so the festival_boundary is never drawn on
  // this read-only map, regardless of cache contents. See lib/trackingAreas.
  const filteredFestivalAreas = useMemo(
    () => selectVisibleTrackingAreas(festivalAreas, dayIndex),
    [festivalAreas, dayIndex],
  );

  // Track when component is ready for map initialization
  const [isComponentReady, setIsComponentReady] = useState(false);

  // Set component ready when subscription is loaded and we have access.
  //
  // Also wait for the two reads the opening camera position comes from. The
  // map used to be built the moment access resolved — before the zones had
  // arrived — so it opened on the Amsterdam fallback, streamed a screenful of
  // satellite tiles nobody wanted, then jumped to the festival and streamed
  // them all again. That second load is the "it fills in from the middle
  // outwards" effect. Built once, at the right place, there is only one load.
  // `isFetched` (not `data`) so a production with no zones still opens.
  const cameraDataReady = festivalAreasFetched && mapSettingsFetched;

  useEffect(() => {
    if (isAccessLoading || !hasLiveTrackingAccess || !briefingId) return;

    // Waiting for the camera data is an optimisation, not a precondition. React
    // Query PAUSES rather than fails a fetch when the browser reports offline,
    // so `isFetched` can stay false indefinitely on a flaky festival
    // connection — and gating the build on it would leave the page with no map
    // at all, no error and nothing to retry. Build anyway after a short wait;
    // the recentre effect below then moves the camera when the data lands,
    // which is exactly the behaviour this page had before.
    const delay = cameraDataReady ? 100 : CAMERA_DATA_GRACE_MS;
    const timer = setTimeout(() => setIsComponentReady(true), delay);
    return () => clearTimeout(timer);
  }, [isAccessLoading, hasLiveTrackingAccess, briefingId, cameraDataReady]);

  // Initialize map with satellite view - waits for component to be ready
  useEffect(() => {
    if (!isComponentReady) {
      return;
    }

    isMounted.current = true;

    const initMap = async () => {
      // Guard: prevent double initialization
      if (isInitializing.current || map.current) {
        return;
      }

      // Retry mechanism if container not ready yet
      if (!mapContainer.current) {
        requestAnimationFrame(() => {
          if (isMounted.current && !map.current) {
            initMap();
          }
        });
        return;
      }

      isInitializing.current = true;

      try {
        const token = await getMapboxToken();

        // Check if still mounted after async operation
        if (!isMounted.current) {
          isInitializing.current = false;
          return;
        }

        mapboxgl.accessToken = token;

        // Ensure container still exists and is empty
        if (!mapContainer.current) {
          isInitializing.current = false;
          return;
        }

        // Synchronously clean the container before creating map
        mapContainer.current.innerHTML = "";

        // Calculate center from festival areas if available
        let center: [number, number] = [4.9041, 52.3676]; // Fallback (Amsterdam)
        let initialBounds: mapboxgl.LngLatBounds | null = null;

        // PRIORITY 1: Use festival areas bounding box center (read current value from data)
        const currentAreas = filteredFestivalAreas;
        if (currentAreas.length > 0) {
          initialBounds = calculateAreasBoundingBox(currentAreas);
          if (initialBounds) {
            center = [
              (initialBounds.getWest() + initialBounds.getEast()) / 2,
              (initialBounds.getSouth() + initialBounds.getNorth()) / 2,
            ];
          }
        }

        // PRIORITY 2: the saved map centre. Same order the recentre effect
        // below uses, so it lands on the same view and has nothing to correct.
        if (
          !initialBounds &&
          mapSettings &&
          mapSettings.center_latitude !== 0 &&
          mapSettings.center_longitude !== 0
        ) {
          center = [mapSettings.center_longitude, mapSettings.center_latitude];
        }

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: "mapbox://styles/mapbox/satellite-streets-v12",
          center,
          // Match the recentre effect below exactly (padding, maxZoom, zoom
          // level) — any difference would show up as a corrective jump the
          // moment that effect runs, and a second round of tile loading.
          zoom: mapSettings?.zoom_level || 15,
          bounds: initialBounds || undefined,
          fitBoundsOptions: { padding: 50, maxZoom: 17 },
        });

        map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

        map.current.on("load", () => {
          setMapLoaded(true);
        });

        isInitializing.current = false;
      } catch (error) {
        console.error(error);
        isInitializing.current = false;
      }
    };

    initMap();

    return () => {
      isMounted.current = false;
      isInitializing.current = false;
      setMapLoaded(false);

      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      currentAreaIds.current = [];
    };
  }, [isComponentReady]);

  // Calculate bounding box from festival areas
  const calculateAreasBoundingBox = (
    areas: Tables<"festival_areas">[],
  ): mapboxgl.LngLatBounds | null => {
    if (areas.length === 0) return null;

    const bounds = new mapboxgl.LngLatBounds();

    areas.forEach((area) => {
      const polygon = parsePolygon(area.polygon);
      if (polygon?.coordinates?.[0]) {
        polygon.coordinates[0].forEach((coord) => {
          bounds.extend(coord as [number, number]);
        });
      }
    });

    return bounds.isEmpty() ? null : bounds;
  };

  // Update map center when briefing/mapSettings/filteredFestivalAreas change
  useEffect(() => {
    const updateMapCenter = async () => {
      if (!map.current || !mapLoaded) return;

      // PRIORITY 1: Use festival areas bounding box (most reliable)
      if (filteredFestivalAreas.length > 0) {
        const bounds = calculateAreasBoundingBox(filteredFestivalAreas);
        if (bounds) {
          map.current.fitBounds(bounds, {
            padding: 50,
            duration: 0, // No animation - map starts at correct location
            maxZoom: 17,
          });
          return;
        }
      }

      let center: [number, number] | null = null;

      // PRIORITY 2: Try to use navigation address from briefing
      if (currentBriefing?.navigation_address) {
        try {
          center = await geocodeAddress(currentBriefing.navigation_address);
        } catch {
          // Could not geocode navigation address
        }
      }

      // PRIORITY 3: Fallback to map settings (if coordinates are valid)
      if (
        !center &&
        mapSettings &&
        mapSettings.center_latitude !== 0 &&
        mapSettings.center_longitude !== 0
      ) {
        center = [mapSettings.center_longitude, mapSettings.center_latitude];
      }

      if (center) {
        map.current.flyTo({
          center,
          zoom: mapSettings?.zoom_level || 15,
          duration: 0, // No animation
        });
      }
    };

    updateMapCenter();
  }, [currentBriefing?.navigation_address, mapSettings, filteredFestivalAreas, mapLoaded]);

  // Helper function to remove all old areas from the map
  const removeOldAreas = () => {
    if (!map.current) return;

    currentAreaIds.current.forEach((oldId) => removeZonePolygonLayers(map.current!, oldId));

    // Defensive sweep: strip any area-* / boundary-* / festival-boundary-*
    // layer or source still on the map but no longer in currentAreaIds
    // (e.g. a stale stage zone added before mapLoaded flipped). The
    // festival-boundary reappearance this used to chase is now fixed at the
    // source — selectVisibleTrackingAreas + the private cache namespace mean
    // a boundary is never fetched or drawn here — so this is now only a
    // best-effort cleanup for orphaned stage-zone layers.
    try {
      const style = map.current.getStyle();
      if (style?.layers) {
        for (const layer of style.layers) {
          if (isOrphanedTrackingLayer(layer.id) && map.current.getLayer(layer.id)) {
            map.current.removeLayer(layer.id);
          }
        }
      }
      if (style?.sources) {
        for (const sourceId of Object.keys(style.sources)) {
          if (isOrphanedTrackingSource(sourceId) && map.current.getSource(sourceId)) {
            map.current.removeSource(sourceId);
          }
        }
      }
    } catch {
      // getStyle can throw mid-style-swap; safe to skip — next render will retry.
    }

    // Clear the tracked IDs
    currentAreaIds.current = [];
  };

  // Add festival areas (stage zones) to map when they change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Get stage labels from briefing
    const stageLabels = new Map<string, string>();
    if (currentBriefing?.stages && Array.isArray(currentBriefing.stages)) {
      (currentBriefing.stages as Array<{ name: string; label: string }>).forEach((stage) => {
        stageLabels.set(stage.name, stage.label);
      });
    }

    const addAreas = () => {
      if (!map.current) return;

      // FIRST: Remove all OLD areas using the tracked IDs
      removeOldAreas();

      // If no areas to add, we're done
      if (filteredFestivalAreas.length === 0) {
        return;
      }

      // Add new areas
      filteredFestivalAreas.forEach((area) => {
        try {
          const parsed = parsePolygon(area.polygon);
          if (!parsed) return;
          const coordinates = parsed.coordinates;
          // Resolve the stage label through the FK: the embed above
          // joins briefing_stages.label, which is the canonical label
          // (single source of truth). festival_areas.name is a
          // denorm cache kept in sync by triggers; briefings.stages
          // JSONB lives on a different cache key
          // (["producer-briefings", userId]) that
          // invalidateBriefingCaches doesn't reach. Both can lag —
          // the FK join can't.
          const fkLabel =
            (area as unknown as { briefing_stages?: { label?: string } | null }).briefing_stages
              ?.label ?? null;
          const areaLabel = resolveZoneLabel({
            areaType: area.area_type,
            fkLabel,
            name: area.name,
            stageId: area.stage_id,
            stageMapLabel: area.stage_id ? stageLabels.get(area.stage_id) : undefined,
          });

          // Fill, border and rounded pill label all use the shared
          // zone look (single source of truth in lib/zoneStyle).
          addZonePolygonLayers(map.current!, {
            areaId: area.id,
            areaType: area.area_type,
            coordinates,
            label: areaLabel,
          });
        } catch (e) {
          console.error(e);
        }
      });

      // Track the new area IDs for future cleanup
      currentAreaIds.current = filteredFestivalAreas.map((area) => area.id);
    };

    // mapLoaded guarantees map is ready, call addAreas directly
    addAreas();
  }, [filteredFestivalAreas, currentBriefing, mapLoaded]);

  // Resize map when sidebar expands/collapses
  useEffect(() => {
    if (!map.current) return;

    const timeout = setTimeout(() => {
      map.current?.resize();
    }, 300);

    return () => clearTimeout(timeout);
  }, [expanded]);

  // Helper function to check if point is inside polygon
  const isPointInPolygon = (point: [number, number], polygon: number[][][]): boolean => {
    try {
      const pt = turf.point(point);
      const poly = turf.polygon(polygon);
      return turf.booleanPointInPolygon(pt, poly);
    } catch {
      return false;
    }
  };

  // Check which crew members are far from their assigned stage
  useEffect(() => {
    const checkCrewStageProximity = async () => {
      if (
        !briefingId ||
        !selectedDay ||
        !crewLocations ||
        crewLocations.length === 0 ||
        filteredFestivalAreas.length === 0
      ) {
        setCrewFarFromStage(new Set());
        return;
      }

      if (!producerSettings.highlight_crew_far_from_stage) {
        setCrewFarFromStage(new Set());
        return;
      }

      const now = new Date();
      const farFromStage = new Set<string>();

      // Fetch current/upcoming assignments for all crew members
      const crewMemberIds = crewLocations.map((loc) => loc.crew_member.id);
      const { data: assignments } = await supabase
        .from("crew_assignments")
        .select("*")
        .eq("briefing_id", briefingId)
        .eq("event_date", selectedDay)
        .eq("is_active", true)
        .in("crew_member_id", crewMemberIds);

      if (!assignments) return;

      // For each crew member, check if they're at their assigned stage.
      // Producers/admins are skipped: their auto-assigned Media Office
      // is a correction-anchor only, not an attendance obligation, and
      // they roam between stages by design. Filming roles (aftermovie,
      // one-taker, photo, recap) are still checked.
      const ROAMING_ROLES = new Set(["producer", "admin"]);
      for (const location of crewLocations) {
        const role = (location.crew_member.function ?? "").toLowerCase();
        if (ROAMING_ROLES.has(role)) continue;

        // Find current or next assignment for this crew member
        const memberAssignments = assignments
          .filter((a) => a.crew_member_id === location.crew_member.id)
          .sort((a, b) => a.filming_start.localeCompare(b.filming_start));

        // Find current assignment (one that's currently active) or next upcoming one within 15 min
        // Midnight-aware: late-night shifts on event_date=X actually happen X+1.
        const currentAssignment = memberAssignments.find((a) => {
          const sp = effectiveShiftStartParts(a.event_date, a.filming_start);
          const ep = effectiveShiftEndParts(a.event_date, a.filming_start, a.filming_end);
          const startTime = wallClockToUtc(sp.date, sp.time, briefingTz);
          const endTime = wallClockToUtc(ep.date, ep.time, briefingTz);
          return now >= startTime && now <= endTime;
        });

        const nextSoonAssignment = !currentAssignment
          ? memberAssignments.find((a) => {
              const sp = effectiveShiftStartParts(a.event_date, a.filming_start);
              const startTime = wallClockToUtc(sp.date, sp.time, briefingTz);
              const diff = (startTime.getTime() - now.getTime()) / 60000; // minutes
              return diff > 0 && diff <= 15; // Within 15 minutes
            })
          : null;

        const relevantAssignment = currentAssignment || nextSoonAssignment;

        if (relevantAssignment) {
          // Find the stage zone for this assignment via the shared
          // FK-first helper (briefing_stage_id ↔ assignment.stage_id
          // post-migration 20260504240000; case-insensitive label
          // fallback for legacy rows).
          const stageZone = filteredFestivalAreas.find((area) =>
            areaMatchesStage(area, {
              id: relevantAssignment.stage_id,
              label: relevantAssignment.stage_name,
              name: relevantAssignment.stage_name,
            }),
          );

          if (stageZone) {
            const parsedPoly = parsePolygon(stageZone.polygon);
            if (!parsedPoly) return;
            const polygon = parsedPoly.coordinates;
            const crewPoint: [number, number] = [location.longitude, location.latitude];

            if (!isPointInPolygon(crewPoint, polygon)) {
              farFromStage.add(location.user_id);
            }
          }
        }
      }

      setCrewFarFromStage(farFromStage);
    };

    checkCrewStageProximity();
  }, [
    briefingId,
    selectedDay,
    crewLocations,
    filteredFestivalAreas,
    producerSettings.highlight_crew_far_from_stage,
  ]);

  // Update markers when crew locations change or selection changes
  useEffect(() => {
    // The map is initialised asynchronously, so crew locations can arrive
    // before it can be drawn on. Gate on isMapReady (map instance + load
    // event) — with mapLoaded kept in the dependency array — so the effect
    // re-runs once the map fires `load`. Without this the markers only
    // appeared after an unrelated dep changed (e.g. clicking a crew member
    // in the sidebar). See isMapReady for the full rationale.
    if (!isMapReady({ hasMap: !!map.current, mapLoaded }) || !crewLocations) return;

    // Reconcile markers in place instead of clearing + recreating every
    // run. The crew_locations_display view's age_seconds ticks each second,
    // so the query data — and this effect — changes on every 5s poll;
    // remove + re-add made the marker visibly blink. Existing markers are
    // mutated (move + restyle); only new crew get a marker created and
    // departed crew get theirs removed (see cleanup loop below).
    const seenUserIds = new Set<string>();

    crewLocations.forEach((location) => {
      seenUserIds.add(location.user_id);
      const isSelected = selectedMarkerId === location.user_id;
      const isFarFromStage =
        producerSettings.highlight_crew_far_from_stage && crewFarFromStage.has(location.user_id);

      // Reuse the existing marker's DOM when present (mutate in place → no
      // blink); otherwise build the shared marker skeleton once.
      const existing = markers.current.get(location.user_id);
      const wrapper =
        (existing?.getElement() as HTMLDivElement | undefined) ??
        createCrewMarkerElement(CREW_MARKER_SIZE.map);

      // The lime ring fills as the crew member's current assignment runs;
      // null leaves an empty grey ring, which is what "free right now"
      // looks like. Shared with the crew app so both maps read the same.
      const shift = shiftStateByCrewMember.get(location.crew_member.id);

      updateCrewMarkerElement(wrapper, {
        initials: getInitials(location.crew_member.name),
        photoUrl: avatarUrlFor(location.user_id),
        ownColor: location.crew_member.color,
        progress: shift?.progress ?? null,
        opacity: markerOpacityForAge(location.age_seconds),
        isSelected,
        isFarFromStage,
      });
      attachCrewMarkerHover(wrapper, isSelected);

      // Click → select + show the Now / Next up popup. Property assignment
      // so the fresh closure replaces the old one on each poll.
      wrapper.onclick = async () => {
        setSelectedMarkerId(location.user_id);
        setSelectedCrewMemberId(location.crew_member.id);

        // Which assignments to show comes from the same shift state that
        // fills the ring around this marker — no second fetch, and no
        // second opinion about what "now" and "next" mean.
        const shiftNow = shiftStateByCrewMember.get(location.crew_member.id);

        // Set times come from the lineup via lineup_id (post-migration
        // 20260504240200); the BEFORE INSERT trigger
        // set_crew_assignment_lineup_id fills it on every artist-type
        // assignment whose filming window fits a slot. A NULL lineup_id
        // means no slot match — show nothing rather than silently rebind
        // by text triple, which would mask data bugs and rename drift.
        //
        // Both assignments are looked up, not just the next one: the set
        // time of the artist being filmed *right now* is the one that
        // used to be missing.
        const lineupIds = [shiftNow?.current?.lineup_id, shiftNow?.next?.lineup_id].filter(
          (id): id is string => !!id,
        );
        const setTimesByLineupId = new Map<string, { set_start: string; set_end: string }>();
        if (lineupIds.length > 0) {
          const { data: lineupRows } = await supabase
            .from("lineup")
            .select("id, set_start, set_end")
            .in("id", lineupIds);
          for (const row of lineupRows || []) {
            setTimesByLineupId.set(row.id, { set_start: row.set_start, set_end: row.set_end });
          }
        }

        const toPopupAssignment = (
          a: CrewShiftAssignment | null | undefined,
        ): CrewPopupAssignment | null => {
          if (!a) return null;
          const setTime = a.lineup_id ? setTimesByLineupId.get(a.lineup_id) : undefined;
          return {
            title: assignmentTitle(a),
            stageName: a.stage_name,
            filmingStart: formatTime(a.filming_start),
            filmingEnd: formatTime(a.filming_end),
            setStart: setTime?.set_start ? formatTime(setTime.set_start) : null,
            setEnd: setTime?.set_end ? formatTime(setTime.set_end) : null,
            notes: a.notes,
          };
        };

        const popupContent = buildCrewMarkerPopupHtml({
          name: location.crew_member.name,
          functionLabel: location.crew_member.function,
          lastSeenLabel: `Last seen ${formatDistanceToNow(new Date(location.timestamp), {
            addSuffix: true,
          })}`,
          current: toPopupAssignment(shiftNow?.current),
          next: toPopupAssignment(shiftNow?.next),
          timeLeftLabel: shiftNow?.timeLeftLabel ?? null,
        });

        // Update marker popup
        const clickedMarker = markers.current.get(location.user_id);
        if (clickedMarker) {
          clickedMarker.setPopup(
            new mapboxgl.Popup({
              offset: 25,
              maxWidth: "320px",
              className: "crew-next-up-popup",
            }).setHTML(popupContent),
          );
          clickedMarker.togglePopup();
        }
      };

      // Use corrected display coords (falls back to raw if no correction
      // applied per the crew_locations_display view's logic).
      // When the producer has show_gps_corrections turned off, render
      // raw coords regardless of what the view computed — the
      // correction system stays running, only this producer's view opts out.
      const useCorrected = producerSettings.show_gps_corrections !== false;
      const markerLng = useCorrected
        ? (location.display_lng ?? location.longitude)
        : location.longitude;
      const markerLat = useCorrected
        ? (location.display_lat ?? location.latitude)
        : location.latitude;
      if (existing) {
        // Move in place — smooth, no remove/add flicker.
        existing.setLngLat([markerLng, markerLat]);
      } else {
        // anchor: "bottom" so the pointer under the marker lands on the
        // actual coordinate instead of the middle of the disc.
        const marker = new mapboxgl.Marker({ element: wrapper, anchor: "bottom" })
          .setLngLat([markerLng, markerLat])
          .addTo(map.current!);
        markers.current.set(location.user_id, marker);
      }
    });

    // Remove markers for crew no longer present in the latest data.
    markers.current.forEach((marker, userId) => {
      if (!seenUserIds.has(userId)) {
        marker.remove();
        markers.current.delete(userId);
      }
    });
  }, [
    crewLocations,
    selectedMarkerId,
    crewFarFromStage,
    producerSettings.highlight_crew_far_from_stage,
    producerSettings.show_gps_corrections,
    mapLoaded,
    crewAvatars,
    shiftStateByCrewMember,
    // Without this the popups keep the old 12h/24h format until the next
    // location update happens to re-run the effect.
    formatTime,
  ]);

  // The producer blue-dot marker is drawn by useProducerLocationMarker
  // (called above) — no separate effect needed here.

  // crew_locations is no longer in supabase_realtime (see migration
  // 20260508160000). Dashboard freshness comes from the React Query
  // refetchInterval above plus the manual refresh button.

  // Show loading while checking subscription (own or, for a co-producer, owner's)
  if (isAccessLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <ProductionSidebar productionId={briefingId!} />

      {!hasLiveTrackingAccess && !isAccessLoading ? (
        <div
          className="flex flex-1 flex-col items-center justify-center px-6 text-center transition-all duration-300"
          style={{ marginLeft: expanded ? "220px" : "72px" }}
        >
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h1 className="mb-3 text-2xl font-bold">Live tracking</h1>
          <p className="mb-6 max-w-md text-muted-foreground">
            The live map needs an active subscription. Draw stage zones, navigate the festival site
            and track your crew during productions.
          </p>
          <Button onClick={() => navigate("/upgrade")}>Choose a plan</Button>
        </div>
      ) : (
        <PageTransition
          className="flex flex-1 flex-col transition-all duration-300"
          style={{ marginLeft: expanded ? "220px" : "72px" }}
        >
          {/* Header */}
          <div className="border-b border-border bg-background px-6 py-4">
            <div className="mb-4">
              <h1 className="text-2xl font-semibold">Live tracking</h1>
              <p className="text-sm text-muted-foreground">
                Real-time crew location and stage presence monitoring
              </p>
            </div>
          </div>

          <div className="flex flex-1">
            {/* Map */}
            <div className="relative flex-1">
              <div ref={mapContainer} className="absolute inset-0 h-full w-full" />

              {/* Current location button */}
              <Button
                size="icon"
                variant="secondary"
                className="absolute left-4 top-4 z-10 bg-card/90 backdrop-blur-sm hover:bg-card"
                onClick={() => {
                  if (producerLocation && map.current) {
                    map.current.flyTo({ center: producerLocation, zoom: 17 });
                  } else {
                    // No fix yet — grab a one-off position to fly there.
                    // useProducerLocationMarker's watch keeps the dot (and
                    // producerLocation) updated from here on.
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        map.current?.flyTo({
                          center: [pos.coords.longitude, pos.coords.latitude],
                          zoom: 17,
                        });
                      },
                      () => {},
                    );
                  }
                }}
                title="Go to my location"
              >
                <Crosshair className="h-4 w-4" />
              </Button>

              {/* Edit Zones button with confirmation dialog */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-4 left-4 z-10 bg-card/90 backdrop-blur-sm hover:bg-card"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit zones
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-border bg-card">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Edit stage zones
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Warning: Modifying stage zones may cause analytics to show inaccurate results
                      for previously tracked data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => navigate(`/briefings/${briefingId}/edit?step=7&shortcut=true`)}
                    >
                      Accept & continue
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Crew List Sidebar */}
            {briefingId && (
              <div className="w-80 border-l bg-background">
                <div className="border-b p-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold">Crew members</h2>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={async () => {
                        setIsRefreshing(true);
                        await refetchLocations();
                        setIsRefreshing(false);
                      }}
                      title="Refresh locations"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${isRefreshing || isLocationsFetching ? "animate-spin" : ""}`}
                      />
                    </Button>
                  </div>
                  {/* Lock B closed: the crew list below will read "No signal"
                      for everyone — say why, or that reads as a breakage. */}
                  {!hasLiveCrew && (
                    <button
                      onClick={() => navigate("/upgrade")}
                      className="mt-2 w-full rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-primary/20"
                    >
                      <span className="font-semibold text-foreground">
                        Live crew positions require PRO or STUDIO.
                      </span>{" "}
                      Your zones and own position stay available — upgrade to see the crew move.
                    </button>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {crewLocations?.length || 0} active ·{" "}
                    {(allCrewMembers?.length || 0) - (crewLocations?.length || 0)} offline
                  </p>
                </div>

                <ScrollArea className="h-[calc(100vh-180px)]">
                  <div className="space-y-3 p-4">
                    {!allCrewMembers ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <Card key={i} className="p-3">
                          <Skeleton className="h-16 w-full" />
                        </Card>
                      ))
                    ) : allCrewMembers.length === 0 ? (
                      <Card className="p-6 text-center">
                        <MapPin className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          No crew members scheduled for this day
                        </p>
                      </Card>
                    ) : (
                      allCrewMembers.map((member) => {
                        // Find GPS location for this crew member
                        const location = crewLocations?.find(
                          (loc) => loc.crew_member.id === member.id,
                        );
                        const hasGPS = !!location;
                        // "Switched off" and "phone is dead" used to look
                        // identical here. See lib/crewTrackingStatus.
                        const status = crewTrackingStatus({
                          lastFixAt: location?.timestamp ?? null,
                          consent: consentByUserId.get(member.user_id ?? ""),
                          now: Date.now(),
                        });
                        const statusLabel = crewTrackingLabel(status);
                        const statusExplanation = crewTrackingExplanation(status);
                        const statusClass = crewTrackingBadgeClass(status);

                        // Sidebar uses display coords so the producer zooms
                        // to the visible (corrected) marker, not the raw fix.
                        // Honors the producer-level show_gps_corrections opt-out.
                        const useCorrected = producerSettings.show_gps_corrections !== false;
                        const sidebarLng = useCorrected
                          ? (location?.display_lng ?? location?.longitude)
                          : location?.longitude;
                        const sidebarLat = useCorrected
                          ? (location?.display_lat ?? location?.latitude)
                          : location?.latitude;
                        const distance =
                          hasGPS && producerLocation && sidebarLng != null && sidebarLat != null
                            ? calculateDistance([sidebarLng, sidebarLat], producerLocation)
                            : null;
                        // Drives both the ring on the avatar and the
                        // "42 min left" line — same source as the map.
                        const shift = shiftStateByCrewMember.get(member.id);

                        return (
                          <Card
                            key={member.id}
                            className={`p-3 transition-colors ${hasGPS ? "cursor-pointer" : ""}`}
                            onClick={() => {
                              if (
                                hasGPS &&
                                map.current &&
                                location &&
                                sidebarLng != null &&
                                sidebarLat != null
                              ) {
                                setSelectedMarkerId(location.user_id);
                                map.current.flyTo({
                                  center: [sidebarLng, sidebarLat],
                                  zoom: 17,
                                });
                                markers.current.get(location.user_id)?.togglePopup();
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              {/* Same marker as on the map, so a dot out
                                  there can be matched to a name in here. */}
                              <CrewMapAvatar
                                name={member.name}
                                photoUrl={member.user_id ? avatarUrlFor(member.user_id) : null}
                                color={member.color}
                                progress={shift?.progress ?? null}
                                size={CREW_MARKER_SIZE.list}
                                className="mt-0.5"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="mb-1 flex items-center gap-2">
                                  <h3 className="truncate font-medium">{member.name}</h3>
                                  <Badge
                                    variant={hasGPS ? "default" : "secondary"}
                                    className={statusClass}
                                  >
                                    {statusLabel}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{member.function}</p>

                                {/* What they're on right now and what
                                    follows it — same source as the ring
                                    and the map popup. */}
                                <CrewShiftLines shift={shift} formatTime={formatTime} />

                                {hasGPS && location && (
                                  <div className="mt-2 flex items-center gap-3 text-xs">
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      <span>
                                        {formatDistanceToNow(new Date(location.timestamp), {
                                          addSuffix: true,
                                        })}
                                      </span>
                                    </div>

                                    {distance && (
                                      <div className="flex items-center gap-1">
                                        <Navigation className="h-3 w-3" />
                                        <span>{formatDistance(distance)}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {statusExplanation && (
                                  <p className="mt-2 text-xs text-muted-foreground">
                                    {statusExplanation}
                                  </p>
                                )}

                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-3 w-full hover:bg-primary hover:text-primary-foreground"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCrewMemberId(member.id);
                                    setScheduleDialogOpen(true);
                                  }}
                                >
                                  Schedule
                                </Button>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        </PageTransition>
      )}

      {/* Schedule Dialog - show NextUpSheet for full schedule */}
      {selectedCrewMemberId && briefingId && selectedDay && (
        <CrewNextUpSheet
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
          crewMemberId={selectedCrewMemberId}
          briefingId={briefingId}
          selectedDay={selectedDay}
        />
      )}
      {briefingId && <EditProductionFAB briefingId={briefingId} />}
    </div>
  );
};

export default LiveTracking;
